/**
 * Blockchain State Context
 *
 * Unified React Context for blockchain state management.
 * Replaces the fragmented GameMakerDataContext with a single source of truth
 * that leverages the enhanced BlockchainDataManager for optimal performance.
 *
 * Features:
 * - Single initialization per wallet connection
 * - Automatic cache invalidation on wallet changes
 * - Event-driven updates with minimal re-renders
 * - Transaction management with current wallet state
 * - Performance monitoring and debugging
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import blockchainDataManager from '../services/BlockchainDataManager.js';
import { provider, signer, createContract, ethersUtils } from '../components/EthersSetup.js';

// Create the context
const BlockchainStateContext = createContext();

/**
 * Custom hook to use blockchain state context
 */
export const useBlockchainState = () => {
  const context = useContext(BlockchainStateContext);
  if (!context) {
    throw new Error('useBlockchainState must be used within a BlockchainStateProvider');
  }
  return context;
};

/**
 * Blockchain State Provider Component
 */
export const BlockchainStateProvider = ({ children }) => {
  // Core state
  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [currentConfig, setCurrentConfig] = useState(null);
  const [error, setError] = useState(null);

  // Wallet state tracking
  const [connectedAccount, setConnectedAccount] = useState(null);
  const [connectedNetwork, setConnectedNetwork] = useState(null);

  // Performance and debugging
  const [stats, setStats] = useState(null);

  // Refs for stability
  const initializationRef = useRef(false);
  const lastConfigRef = useRef(null);

  /**
   * Initialize BlockchainDataManager with configuration
   */
  const initialize = useCallback(async (config) => {
    const {
      gameMakerAddress,
      gameMakerAbi,
      gameAddresses = {},
      playerAccount,
      provider: configProvider,
      createContract: configCreateContract,
      ethersUtils: configEthersUtils
    } = config;

    // Validate required configuration
    if (!gameMakerAddress || !gameMakerAbi || !playerAccount) {
      const missingFields = [];
      if (!gameMakerAddress) missingFields.push('gameMakerAddress');
      if (!gameMakerAbi) missingFields.push('gameMakerAbi');
      if (!playerAccount) missingFields.push('playerAccount');

      const errorMsg = `Missing required configuration: ${missingFields.join(', ')}`;
      console.error('❌ BlockchainStateProvider:', errorMsg);
      setError(errorMsg);
      return false;
    }

    // Check if this is the same configuration (avoid unnecessary re-initialization)
    const configKey = `${gameMakerAddress}-${playerAccount}-${configProvider?.network?.chainId || 'unknown'}`;
    if (lastConfigRef.current === configKey && isInitialized) {
      console.log('🔄 BlockchainStateProvider: Configuration unchanged, skipping re-initialization');
      return true;
    }

    // Prevent concurrent initializations
    if (initializationRef.current) {
      console.log('⏳ BlockchainStateProvider: Already initializing, skipping...');
      return false;
    }

    try {
      setIsInitializing(true);
      setError(null);
      initializationRef.current = true;

      console.log('🚀 BlockchainStateProvider: Initializing with config:', {
        gameMakerAddress,
        playerAccount,
        gameCount: Object.keys(gameAddresses).length,
        chainId: configProvider?.network?.chainId
      });

      // Initialize the BlockchainDataManager
      await blockchainDataManager.initialize({
        gameMakerAddress,
        gameMakerAbi,
        gameAddresses,
        provider: configProvider,
        playerAccount
      });

      // Update state
      setCurrentConfig(config);
      setConnectedAccount(playerAccount);
      setConnectedNetwork(configProvider?.network?.chainId || null);
      setIsInitialized(true);
      lastConfigRef.current = configKey;

      console.log('✅ BlockchainStateProvider: Successfully initialized');
      return true;

    } catch (error) {
      console.error('❌ BlockchainStateProvider: Initialization failed:', error);
      setError(error.message || 'Failed to initialize blockchain state');
      setIsInitialized(false);
      return false;

    } finally {
      setIsInitializing(false);
      initializationRef.current = false;
    }
  }, [isInitialized]);

  /**
   * Reset state when wallet changes
   */
  const reset = useCallback(() => {
    console.log('🔄 BlockchainStateProvider: Resetting state...');

    // Destroy current manager instance
    blockchainDataManager.destroy();

    // Reset local state
    setIsInitialized(false);
    setIsInitializing(false);
    setCurrentConfig(null);
    setConnectedAccount(null);
    setConnectedNetwork(null);
    setError(null);
    setStats(null);

    // Reset refs
    lastConfigRef.current = null;
    initializationRef.current = false;

    console.log('✅ BlockchainStateProvider: State reset complete');
  }, []);

  /**
   * Transaction management with current wallet state
   */
  const createTransaction = useCallback(async (contractAddress, contractAbi, methodName, args = [], options = {}) => {
    if (!signer) {
      throw new Error('No signer available for transaction');
    }

    if (!connectedAccount) {
      throw new Error('No connected account for transaction');
    }

    try {
      console.log(`🔗 Creating transaction: ${methodName} on ${contractAddress}`);

      // Create contract with current signer
      const contract = await createContract(contractAddress, contractAbi, true);
      if (!contract) {
        throw new Error('Failed to create contract instance for transaction');
      }

      // Estimate gas
      const estimatedGas = await contract.estimateGas[methodName](...args, options);
      const gasLimit = Math.floor(Number(estimatedGas) * 1.2); // 20% buffer

      // Get current gas price
      const gasPrice = await provider.getGasPrice();
      const gasPriceBid = Math.floor(Number(gasPrice) * 1.2); // 20% buffer

      // Execute transaction
      const tx = await contract[methodName](...args, {
        ...options,
        gasLimit,
        gasPrice: gasPriceBid
      });

      console.log(`✅ Transaction submitted: ${tx.hash}`);
      return tx;

    } catch (error) {
      console.error(`❌ Transaction failed: ${methodName}`, error);
      throw error;
    }
  }, [connectedAccount]);

  /**
   * Convenience transaction methods
   */
  const acceptOffer = useCallback(async (offerId, offerValue) => {
    if (!currentConfig) throw new Error('Blockchain state not initialized');

    // Try to get game name from offer data to notify data manager
    try {
      const offers = blockchainDataManager.getCachedData('offers')?.data || [];
      const offer = offers.find(o => o.offerId.toString() === offerId.toString());
      if (offer?.gameName) {
        blockchainDataManager.notifyTransactionInProgress(offer.gameName);
      }
    } catch (error) {
      console.warn('⚠️ Could not notify data manager for acceptOffer:', error);
    }

    return createTransaction(
      currentConfig.gameMakerAddress,
      currentConfig.gameMakerAbi,
      'acceptGame',
      [offerId],
      { value: offerValue }
    );
  }, [currentConfig, createTransaction]);

  const killGame = useCallback(async (offerId) => {
    if (!currentConfig) throw new Error('Blockchain state not initialized');

    return createTransaction(
      currentConfig.gameMakerAddress,
      currentConfig.gameMakerAbi,
      'killGame',
      [offerId]
    );
  }, [currentConfig, createTransaction]);

  const makeOffer = useCallback(async (gameName, player2Bet, timeout, gameParams, targetAddress = '0x0000000000000000000000000000000000000000', player1Bet) => {
    if (!currentConfig) throw new Error('Blockchain state not initialized');

    // Notify data manager that a transaction involving this game is starting
    blockchainDataManager.notifyTransactionInProgress(gameName);

    return createTransaction(
      currentConfig.gameMakerAddress,
      currentConfig.gameMakerAbi,
      'offerGame',
      [gameName, player2Bet, timeout, gameParams, targetAddress],
      { value: player1Bet }
    );
  }, [currentConfig, createTransaction]);

  const offerDraw = useCallback(async (offerId, payout1, payout2) => {
    if (!currentConfig) throw new Error('Blockchain state not initialized');

    const payout1Wei = ethersUtils.toWei(payout1.toString(), 'ether');
    const payout2Wei = ethersUtils.toWei(payout2.toString(), 'ether');

    return createTransaction(
      currentConfig.gameMakerAddress,
      currentConfig.gameMakerAbi,
      'offerDraw',
      [offerId, payout1Wei, payout2Wei]
    );
  }, [currentConfig, createTransaction]);

  const acceptDrawOffer = useCallback(async (offerId, otherPlayerAddress) => {
    if (!currentConfig) throw new Error('Blockchain state not initialized');

    return createTransaction(
      currentConfig.gameMakerAddress,
      currentConfig.gameMakerAbi,
      'acceptDrawOffer',
      [offerId, otherPlayerAddress]
    );
  }, [currentConfig, createTransaction]);

  const withdrawDrawOffer = useCallback(async (offerId) => {
    if (!currentConfig) throw new Error('Blockchain state not initialized');

    return createTransaction(
      currentConfig.gameMakerAddress,
      currentConfig.gameMakerAbi,
      'withdrawDrawOffer',
      [offerId]
    );
  }, [currentConfig, createTransaction]);

  /**
   * Performance monitoring
   */
  useEffect(() => {
    if (!isInitialized) return;

    const interval = setInterval(() => {
      const currentStats = blockchainDataManager.getStats();
      setStats(currentStats);
    }, 10000); // Update stats every 10 seconds

    return () => clearInterval(interval);
  }, [isInitialized]);

  /**
   * Wallet change detection
   */
  useEffect(() => {
    if (!connectedAccount || !currentConfig) return;

    // Check if the account has changed
    if (currentConfig.playerAccount !== connectedAccount) {
      console.log('👤 Account changed, resetting blockchain state');
      reset();
    }
  }, [connectedAccount, currentConfig, reset]);

  /**
   * Context value - carefully memoized to prevent unnecessary re-renders
   */
  const contextValue = useMemo(() => ({
    // State
    isInitialized,
    isInitializing,
    error,
    connectedAccount,
    connectedNetwork,
    currentConfig,
    stats,

    // Actions
    initialize,
    reset,

    // Transactions
    createTransaction,
    acceptOffer,
    killGame,
    makeOffer,
    offerDraw,
    acceptDrawOffer,
    withdrawDrawOffer,

    // Utilities
    isReady: isInitialized && !error && !!connectedAccount
  }), [
    isInitialized,
    isInitializing,
    error,
    connectedAccount,
    connectedNetwork,
    currentConfig,
    stats,
    initialize,
    reset,
    createTransaction,
    acceptOffer,
    killGame,
    makeOffer,
    offerDraw,
    acceptDrawOffer,
    withdrawDrawOffer
  ]);

  return (
    <BlockchainStateContext.Provider value={contextValue}>
      {children}
    </BlockchainStateContext.Provider>
  );
};

/**
 * HOC for components that need blockchain state
 */
export const withBlockchainState = (Component) => {
  return (props) => (
    <BlockchainStateProvider>
      <Component {...props} />
    </BlockchainStateProvider>
  );
};

export default BlockchainStateContext;